// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { createContext } from 'react';
import type { CopilotToolCallUserDecisionEnum } from 'schema';
import type { Answer, ToolCall } from '../../../CopilotContext';

export type ToolCallDecision = {
    toolCallId: string;
    decision: CopilotToolCallUserDecisionEnum;
};

export type ToolCallPayload = {
    result: string | null;
    modelResult: string | null;
};

// Identifies a call across turns: wire ids repeat, so the issuing row is part of it
export const toolCallKey = (call: Pick<ToolCall, 'messageId' | 'id'>): string => `${call.messageId}:${call.id}`;

/**
 * What the tool-call rendering needs from the chat: the actions, which talk to the server,
 * and the bits of run state that are not on the calls themselves.
 */
export type ToolCallActions = {
    decide: (answer: Answer, decisions: ToolCallDecision[]) => void;
    run: (answer: Answer) => void;
    // The stored payload and the replayed string, fetched on demand
    fetchResult: (answer: Answer, call: ToolCall) => Promise<ToolCallPayload>;
    // toolCallKey() of every call whose decide request is in flight
    deciding: ReadonlySet<string>;
    // Ids of the assistant rows whose batch is being run from this tab
    runInFlight: ReadonlySet<string>;
    // Ids of the assistant rows whose last run request failed: an all-automatic batch shows
    // Run only then
    failedRuns: ReadonlySet<string>;
    // Ids of the assistant rows whose failed run is being rechecked by reloading the history
    checkingRuns: ReadonlySet<string>;
    // The conversation's latest persisted assistant row: only its batch can be run
    latestRowId: string | null;
    // The key of the conversation's last entry: only a batch nothing follows - not even
    // the admin's own question, which has no id yet - is waiting for the next message
    lastAnswerKey: string | null;
};

export const ToolCallContext = createContext<ToolCallActions>({
    decide: () => { },
    run: () => { },
    fetchResult: () => Promise.resolve({ result: null, modelResult: null }),
    deciding: new Set(),
    runInFlight: new Set(),
    failedRuns: new Set(),
    checkingRuns: new Set(),
    latestRowId: null,
    lastAnswerKey: null,
});
